import { DefaultButton, Stack, Text, getTheme } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import React from 'react';
import IvicosStrings from 'kits/language/stringKit';
import AccessManagement from './AccessManagement';
import Calendar from './Calendar';

const ExternalDoors: React.FC = () => {
    const [isAccessManagementOpen, { setTrue: openAccessManagement, setFalse: closeAccessManagement }] = useBoolean(false);
    const [isCalendarOpen, { setTrue: openCalendar, setFalse: closeCalendar }] = useBoolean(false);
    const theme = getTheme();
    const dateLabel = String(new Date().getDate());

    const [now, setNow] = React.useState<Date>(new Date());

    React.useEffect(() => {
        const intervalId = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(intervalId);
    }, []);

    const locale = IvicosStrings.getLanguage() === 'de' ? 'de-DE' : 'en-US';
    const formattedDate = now.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const formattedTime = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
    const currentDateTimeLabel = IvicosStrings.externalDoorsCurrentDateTime.replace('[date]', formattedDate).replace('[time]', formattedTime);

    const renderAccessCalendarIcon = () => (
        <Stack
            aria-hidden
            styles={{
                root: {
                    width: 20,
                    height: 20,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.neutralSecondary}`,
                    overflow: 'hidden',
                    background: theme.palette.white,
                    marginRight: 6
                }
            }}
        >
            <div style={{ height: 5, background: theme.palette.themePrimary }} />
            <Text
                styles={{
                    root: {
                        flex: 1,
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: '14px',
                        textAlign: 'center',
                        color: theme.palette.neutralPrimary
                    }
                }}
            >
                {dateLabel}
            </Text>
        </Stack>
    );

    return (
        <Stack
            tokens={{ childrenGap: 40 }}
            style={{
                width: '100%',
                height: '500px',
                padding: '20px 0'
            }}
        >
            {/* Header - Centered */}
            <Stack style={{ width: '100%' }}>
                <Text
                    variant="mediumPlus"
                    style={{
                        fontWeight: 600,
                        width: '100%'
                    }}
                >
                    {IvicosStrings.externalDoorsAdminCenterTitle}
                </Text>
                <Stack
                    style={{
                        width: '100%',
                        background: theme.palette.neutralLighter,
                        padding: '8px 12px',
                        marginTop: 4
                    }}
                >
                    <Text
                        variant="small"
                        style={{
                            color: theme.palette.neutralSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontWeight: 600
                        }}
                    >
                        {currentDateTimeLabel}
                    </Text>
                </Stack>
            </Stack>

            {/* Two Column Layout - Side by Side */}
            <Stack
                horizontal
                tokens={{ childrenGap: 48 }}
                style={{
                    width: '100%',
                    alignItems: 'stretch'
                }}
            >
                {/* Left Section: Create/edit access */}
                <Stack
                    tokens={{ childrenGap: 16 }}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                    }}
                >
                    <Stack tokens={{ childrenGap: 16 }}>
                        <Text
                            variant="medium"
                            style={{
                                fontWeight: 600,
                                marginBottom: 0
                            }}
                        >
                            {IvicosStrings.externalDoorsCreateEditAccessPassesTitle}
                        </Text>
                        <Stack tokens={{ childrenGap: 8 }} style={{ marginLeft: 16 }}>
                            <Text variant="medium" style={{ lineHeight: 1.5 }}>
                                • {IvicosStrings.externalDoorsSecurityLevels}
                            </Text>
                            <Text variant="medium" style={{ lineHeight: 1.5 }}>
                                • {IvicosStrings.externalDoorsAccessMethods}
                            </Text>
                            <Text variant="medium" style={{ lineHeight: 1.5 }}>
                                • {IvicosStrings.externalDoorsBankHolidays}
                            </Text>
                            <Text variant="medium" style={{ lineHeight: 1.5 }}>
                                • {IvicosStrings.externalDoorsOpeningHours}
                            </Text>
                        </Stack>
                    </Stack>
                    <DefaultButton
                        text={IvicosStrings.externalDoorsCreateEditExteriorDoorsButton}
                        iconProps={{ iconName: 'Edit' }}
                        styles={{
                            root: {
                                width: 'fit-content'
                            }
                        }}
                        onClick={openAccessManagement}
                    />
                </Stack>

                {/* Right Section: Overview of existing accesses */}
                <Stack
                    tokens={{ childrenGap: 16 }}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                    }}
                >
                    <Stack tokens={{ childrenGap: 16 }}>
                        <Text
                            variant="medium"
                            style={{
                                fontWeight: 600,
                                marginBottom: 0
                            }}
                        >
                            {IvicosStrings.externalDoorsOverviewTitle}
                        </Text>
                        <Stack tokens={{ childrenGap: 8 }} style={{ marginLeft: 16 }}>
                            <Text variant="medium" style={{ lineHeight: 1.5 }}>
                                • {IvicosStrings.externalDoorsOverviewSecurityComfort}
                            </Text>
                            <Text variant="medium" style={{ lineHeight: 1.5 }}>
                                • {IvicosStrings.externalDoorsOverviewControl}
                            </Text>
                        </Stack>
                    </Stack>
                    <DefaultButton
                        text={IvicosStrings.externalDoorsEnterControlRoomButton}
                        onRenderIcon={renderAccessCalendarIcon}
                        styles={{
                            root: {
                                width: 'fit-content'
                            }
                        }}
                        onClick={openCalendar}
                    />
                </Stack>
            </Stack>

            {/* Access Management Modal */}
            {isAccessManagementOpen && <AccessManagement onClose={closeAccessManagement} />}

            {/* Calendar Modal */}
            {isCalendarOpen && <Calendar onClose={closeCalendar} />}
        </Stack>
    );
};

export default ExternalDoors;
